// The atlas: five thousand years as one scroll you can touch. See docs/design/the-scroll.md.

import "@fontsource/eb-garamond/400.css";
import "@fontsource/eb-garamond/600.css";
import "@fontsource/eb-garamond/400-italic.css";
import "@fontsource/im-fell-english/400.css";
import "@fontsource/im-fell-english/400-italic.css";
import "@fontsource/im-fell-english-sc/400.css";
import "./styles.css";

import { buildChart, fmtYear, holderOf, neighboursOf } from "@hoee/chart";
import type { Age, Realm } from "@hoee/chart";
import type { Timeline } from "@hoee/schema";
import { ChronicleView, loadChronicle, loadPages } from "./chronicle.ts";
import type { Hit } from "./chronicle.ts";
import { Labels } from "./gl/labels.ts";
import { paperLayer, paperTexture } from "./gl/paper.ts";
import { realmsLayer } from "./gl/realms.ts";
import { Rules } from "./gl/rules.ts";
import { Stage } from "./gl/stage.ts";
import { attachInput } from "./input.ts";
import { DAY, FONTS, NIGHT } from "./theme.ts";
import { Camera } from "./view/camera.ts";
import type { Plot } from "./view/camera.ts";
import { Spring } from "./view/spring.ts";
import { ErasBand } from "./ui/eras.ts";
import { LandsPanel } from "./ui/lands.ts";
import { imageFor, plateFor, prefetch } from "./ui/plates.ts";
import type { Plate } from "./ui/plates.ts";
import { ReadingPanel } from "./ui/reading.ts";
import { Seek } from "./ui/seek.ts";
import type { Target } from "./ui/seek.ts";
import { spanText, WorldText } from "./world.ts";

const byId = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`The page has no #${id}.`);
  return node as T;
};

// Fonts must be ready before any text is typeset into the GPU's atlas.
await Promise.all(
  [
    `600 20px ${FONTS.text}`,
    `400 20px ${FONTS.text}`,
    `italic 400 20px ${FONTS.text}`,
    `20px ${FONTS.display}`,
    `italic 20px ${FONTS.display}`,
    `20px ${FONTS.displaySc}`,
  ].map((f) => document.fonts.load(f, "Aa Đại Việt Ōtomo").catch(() => [])),
);

// Checked at build time against the schema (packages/schema), so it is trusted here.
const timeline = (await fetch("data/timeline.json").then((r) => r.json())) as Timeline;
const chart = buildChart(timeline);
/** The chart ends at AD 2000: nothing is framed past it. */
const END = chart.axis.end;

const canvas = byId<HTMLCanvasElement>("world");
const stage = new Stage(canvas);
/** Room kept clear at the right for the notes beside the scroll (Read more), in px; it opens smoothly. */
const room = new Spring(0, 0.09);
const plotOf = (): Plot => {
  const left = innerWidth < 700 ? 120 : 216;
  return { left, top: 112, width: innerWidth - left - 10 - room.value, height: innerHeight - 112 - 12 };
};
const camera = new Camera(chart, plotOf());

const paper = paperTexture();
stage.scene.add(paperLayer(stage.uniforms, paper));
const realms = realmsLayer(chart, stage.uniforms, paper);
stage.scene.add(realms.shadow, realms.mesh);
const rules = new Rules(stage.uniforms, 50);
const overlay = new Rules(stage.uniforms, 150);
const labels = new Labels(stage.uniforms, stage.renderer);
stage.scene.add(rules.mesh, overlay.mesh, ...labels.meshes);
const text = new WorldText(chart, labels, rules, overlay);
const chronicle = new ChronicleView(chart, labels, rules);

// ---------- Edition ----------

const night = new Spring(0, 0.2);
const themeBtn = byId<HTMLButtonElement>("theme");
function setTheme(dark: boolean, animate = true) {
  const root = document.documentElement;
  root.dataset.theme = dark ? "dark" : "light";
  // The pigments, for the few HTML things painted in a realm's colour (Seek's chips).
  (dark ? NIGHT : DAY).pigments.forEach((c, i) =>
    root.style.setProperty(`--p${i}`, `rgb(${c.map((v) => Math.round(v * 255)).join(" ")})`),
  );
  const glyph = document.createElement("span");
  glyph.setAttribute("aria-hidden", "true");
  glyph.textContent = dark ? "☀" : "☾";
  const label = document.createElement("span");
  label.className = "label";
  label.textContent = dark ? " Day" : " Night";
  themeBtn.replaceChildren(glyph, label);
  themeBtn.setAttribute("aria-label", dark ? "Day edition" : "Night edition");
  night.target = dark ? 1 : 0;
  if (!animate) night.set(night.target);
  stage.invalidate();
}
setTheme(false, false);
themeBtn.addEventListener("click", () => setTheme(night.target < 0.5));

// ---------- Opening a realm ----------

const focusMix = new Spring(0, 0.14);
let focused: Realm | null = null;
/** The land brought forward, if any: on its own, or as the place a realm was opened from. */
let land: number | null = null;
let returnTo: ReturnType<Camera["bookmark"]> | null = null;
const trail = byId("trail");

/** When the reader last scrolled, dragged or zoomed (not tapped): only that may hand a realm over. */
let lastInput = -Infinity;
/** Every deliberate jump goes through here: it cancels any handover the scrolling just before might start. */
function flyTo(from: number, to: number, room?: number) {
  // Never "just now", even in the first moments after the page loads (performance.now() starts at 0).
  lastInput = -Infinity;
  camera.frameYears(from, to, room);
}

/** Fills the screen with a realm's lifetime, leaving only a sliver of the years either side. */
function frameRealm(realm: Realm) {
  flyTo(realm.start, Math.min(realm.end, END), 0.01);
}

/**
 * Opens a realm. `frame` flies to its lifetime; a handover while scrolling (frame: false) keeps the view where
 * the reader has taken it and only moves the lanes.
 */
function focus(realm: Realm | null, { frame = true }: { frame?: boolean } = {}) {
  if (realm === focused) return;
  closeCard();
  if (reading.about && reading.about.realm !== realm) reading.close();
  if (realm) {
    if (!focused && land == null) returnTo = camera.bookmark();
    focused = realm;
    camera.focusRealm(realm.home, realm.lanes);
    if (frame) frameRealm(realm);
    // Its neighbours along its land are fetched now, so scrolling into them never waits.
    for (const n of neighboursOf(chart, realm)) void loadPages(n.id);
    focusMix.target = 1;
    realms.uniforms.uFocus.value = realm.index;
    chronicle.show(realm, null);
    void loadPages(realm.id).then((pages) => {
      if (focused === realm) {
        chronicle.show(realm, pages);
        stage.invalidate();
      }
    });
    trailKey = "";
    trail.classList.add("shown");
  } else {
    focused = null;
    focusMix.target = 0;
    if (land != null) showLand(land);
    else {
      camera.releaseLanes();
      if (returnTo) camera.restore(returnTo);
      returnTo = null;
      trail.classList.remove("shown");
    }
  }
  text.focus = realm ?? text.focus;
  stage.invalidate();
}

function go(realm: Realm | null) {
  const hash = realm ? `#/${realm.id}` : land != null ? `#/land/${chart.columns[land]?.id}` : "#/";
  if (location.hash !== hash) history.pushState(null, "", hash);
  focus(realm);
}

/**
 * Scrolling along an open realm's land past its years hands it over to whoever held that land next (or before),
 * so the page is always filled with the realm under the middle of the screen. A little beyond the border first,
 * so it does not flicker back and forth on the line.
 */
function handOver() {
  // Only your own scrolling hands a realm over; the camera's own flights (framing a realm, an age) never do.
  if (!focused || focusMix.target < 1 || performance.now() - lastInput > 600) return;
  const { x0, x1 } = camera.inner;
  const from = camera.yearAt(x0),
    to = camera.yearAt(x1);
  const mid = (from + to) / 2,
    margin = 0.03 * (to - from);
  const { home } = focused;
  if (mid >= home.from - margin && mid < home.to + margin) return;
  const next = holderOf(chart, home, mid);
  // The page stays on the land you are reading: a realm whose own home is another land (a brief conquest, say)
  // is passed over rather than pulling the lanes elsewhere.
  if (!next || next === focused || next.home.lane !== home.lane) return;
  // Reading the whole realm (not inside one of its ages)? Then the next one is shown whole too, once the
  // scrolling comes to rest. Inside an age, the zoom is kept, so close reading carries on into the neighbour.
  const wholeView = chronicle.trail.length === 0;
  focus(next, { frame: false });
  history.replaceState(null, "", `#/${next.id}`);
  frameWhenQuiet = wholeView ? next : null;
}
/** A realm to frame whole as soon as the reader stops scrolling. */
let frameWhenQuiet: Realm | null = null;
function settleHandover() {
  if (!frameWhenQuiet) return;
  if (frameWhenQuiet !== focused) {
    frameWhenQuiet = null;
    return;
  }
  if (camera.held || performance.now() - lastInput < 220) return;
  frameRealm(frameWhenQuiet);
  frameWhenQuiet = null;
}

// ---------- Lands: one land's whole history ----------

/** The years a land's realms cover, from the first that held it to today. */
function landYears(lane: number): [number, number] {
  const blocks = chart.blocks.filter((b) => Math.floor(b.x0) === lane);
  return [Math.max(chart.axis.start, Math.min(...blocks.map((b) => b.cy0))), END];
}
function showLand(lane: number) {
  land = lane;
  camera.focusLand(lane);
  const [from, to] = landYears(lane);
  flyTo(from, to, 0.02);
  trailKey = "";
  trail.classList.add("shown");
  stage.invalidate();
}
/** Brings a land forward (or, clicked again, lets it go back among the others). */
function goLand(lane: number | null) {
  if (lane != null && lane === land && !focused) lane = null;
  if (focused) {
    focused = null;
    focusMix.target = 0;
  }
  closeCard();
  reading.close();
  if (lane == null) {
    land = null;
    camera.releaseLanes();
    if (returnTo) camera.restore(returnTo);
    returnTo = null;
    trail.classList.remove("shown");
  } else {
    if (land == null) returnTo = camera.bookmark();
    showLand(lane);
  }
  const hash = lane != null ? `#/land/${chart.columns[lane]?.id}` : "#/";
  if (location.hash !== hash) history.pushState(null, "", hash);
  stage.invalidate();
}

function fromHash() {
  const [id = "", ...agePath] = location.hash.replace(/^#\/?/, "").split("/");
  if (id === "land") {
    const lane = chart.columns.findIndex((c) => c.id === agePath[0]);
    if (focused) focus(null);
    if (lane >= 0) {
      if (land == null) returnTo = camera.bookmark();
      showLand(lane);
    }
    return;
  }
  const realm = chart.realmById.get(id) ?? null;
  if (!realm && land != null && !focused) {
    land = null;
    camera.releaseLanes();
    if (returnTo) camera.restore(returnTo);
    returnTo = null;
    trail.classList.remove("shown");
    stage.invalidate();
    return;
  }
  focus(realm);
  // A link can name an age inside the realm: open it once the chronicle is here.
  const ageId = agePath.at(-1);
  if (realm && ageId)
    void loadPages(realm.id).then((pages) => {
      const age = pages?.ages.find((a) => a.period.id === ageId);
      if (age && focused === realm) enterAge(age);
    });
}

// ---------- Ages: diving deeper inside a realm ----------

/** Flies to an age, so it fills the screen and becomes where you are. */
function enterAge(age: Age) {
  closeCard();
  flyTo(age.period.from, age.period.to, 0.04);
  stage.invalidate();
}
/** Back out one level: from an age to its parent, from the outermost age to the whole realm. */
function climb(): boolean {
  if (!focused) return false;
  const here = chronicle.trail.at(-1);
  if (!here) return false;
  if (here.parent) enterAge(here.parent);
  else frameRealm(focused);
  stage.invalidate();
  return true;
}
// The trail at the top follows where you are, and every step of it can be clicked.
let trailKey = "";
function syncTrail() {
  if (!focused && land == null) return;
  const ages = focused ? chronicle.trail : [];
  const key = `${land}/${focused?.id ?? ""}/${ages.map((a) => a.period.id).join("/")}`;
  if (key === trailKey) return;
  trailKey = key;
  const crumb = (text: string, onClick: (() => void) | null) => {
    const node = document.createElement(onClick ? "button" : "span");
    node.textContent = text;
    if (onClick) {
      (node as HTMLButtonElement).type = "button";
      node.addEventListener("click", onClick);
    }
    return node;
  };
  const realm = focused;
  const here = land;
  trail.replaceChildren(
    crumb("✦ The World", () => goLand(null)),
    ...(here != null ? [crumb(chart.columns[here]?.name ?? "", realm ? () => go(null) : null)] : []),
    ...(realm
      ? [
          crumb(realm.name, ages.length ? () => frameRealm(realm) : null),
          ...ages.map((a, i) => crumb(a.period.name, i < ages.length - 1 ? () => enterAge(a) : null)),
        ]
      : []),
  );
  // Long trails shorten their middle steps; the full name is kept as a tooltip.
  for (const node of trail.children) (node as HTMLElement).title = node.textContent ?? "";
  trail.classList.toggle("long", ages.length > 1);
  // The address follows too, so a link opens the same place.
  if (realm) history.replaceState(null, "", `#/${[realm.id, ...ages.map((a) => a.period.id)].join("/")}`);
}
window.addEventListener("popstate", fromHash);

// ---------- Cards: the full story of an event or a life ----------

const card = byId("card");
let cardId: string | null = null;
const el = <K extends keyof HTMLElementTagNameMap>(tag: K, cls: string, text?: string) => {
  const node = document.createElement(tag);
  node.className = cls;
  if (text) node.textContent = text;
  return node;
};
const wikiUrl = (t: string) => `https://en.wikipedia.org/wiki/${encodeURIComponent(t.replace(/ /g, "_"))}`;

function showCard(hit: Hit, plate: Plate | null, opening: number) {
  const parts: HTMLElement[] = [];
  let wiki: string | undefined;
  if (hit.happening) {
    const ev = hit.happening.event;
    cardId = ev.id;
    wiki = ev.wiki;
    const when = ev.date ? `${ev.date} ${fmtYear(ev.year)}` : `${ev.approx ? "c. " : ""}${fmtYear(ev.year)}`;
    parts.push(
      el("p", "card-when", [when, ev.place].filter(Boolean).join(" · ")),
      el("h2", "card-title", ev.title),
      el("p", "card-text", ev.text),
    );
  } else if (hit.life) {
    const p = hit.life.person;
    cardId = p.id;
    wiki = p.wiki;
    const span =
      p.born != null || p.died != null
        ? `${p.born != null ? fmtYear(p.born) : "?"} – ${p.died != null ? fmtYear(p.died) : "?"}`
        : `fl. ${fmtYear(p.floruit ?? 0)}`;
    parts.push(
      el("p", "card-when", `${p.approx ? "c. " : ""}${span}`),
      el("h2", "card-title", p.name),
      el("p", "card-text", p.role),
    );
    if (p.dateNote) parts.push(el("p", "card-note", p.dateNote));
  }
  if (wiki) {
    const link = el("a", "card-link", "Read more on Wikipedia →");
    link.setAttribute("href", wikiUrl(wiki));
    link.setAttribute("target", "_blank");
    link.setAttribute("rel", "noopener");
    parts.push(link);
  }
  const close = el("button", "card-close", "×");
  close.setAttribute("aria-label", "Close");
  close.addEventListener("click", () => closeCard());
  card.replaceChildren(close, ...parts);
  if (plate) hangPlate(plate, opening, false);
  card.hidden = false;
  requestAnimationFrame(() => card.classList.add("open"));
  chronicle.selected = cardId;
  placeCard(true);
  stage.invalidate();
}
function closeCard() {
  cardOpening++;
  if (cardId == null) return;
  cardId = null;
  chronicle.selected = null;
  card.classList.remove("open");
  setTimeout(() => {
    if (cardId == null) card.hidden = true;
  }, 220);
  stage.invalidate();
}
/** Where the picture goes: a space of its final size, in which the picture fades in once it has loaded. */
function hangPlate(plate: Plate, opening: number, late: boolean) {
  const inner = Math.min(400, innerWidth - 20) - 44 + 16;
  const h = Math.round(Math.min(320, (inner * plate.height) / plate.width));
  const w = Math.round((h * plate.width) / plate.height);
  const figure = el("figure", "card-plate");
  figure.style.height = late ? "0px" : `${h}px`;
  const frame = el("div", "card-plate-frame");
  frame.style.width = `${w}px`;
  frame.style.height = `${h}px`;
  figure.append(frame);
  card.querySelector(".card-close")?.after(figure);
  // Arriving after the card opened, the space opens once, smoothly, before the picture fades in.
  if (late) requestAnimationFrame(() => (figure.style.height = `${h}px`));
  void imageFor(plate).then((img) => {
    if (cardOpening !== opening) return;
    if (!img) {
      figure.remove();
      return;
    }
    const shown = img.cloneNode() as HTMLImageElement;
    frame.append(shown);
    requestAnimationFrame(() => frame.classList.add("shown"));
  });
}

/** Opens a card: at once if its picture's size is known (it usually is, from pointing at it), else soon. */
let cardOpening = 0;
function openCard(hit: Hit) {
  const opening = ++cardOpening;
  const wiki = hit.happening?.event.wiki ?? hit.life?.person.wiki;
  if (!wiki) return showCard(hit, null, opening);
  let shown = false;
  const plate = plateFor(wiki);
  // Wait a moment for the picture's size, so the card opens at its final size; never longer than that.
  const timer = setTimeout(() => {
    if (shown || opening !== cardOpening) return;
    shown = true;
    showCard(hit, null, opening);
    void plate.then((p) => p && opening === cardOpening && cardId != null && hangPlate(p, opening, true));
  }, 400);
  void plate.then((p) => {
    if (shown || opening !== cardOpening) return;
    shown = true;
    clearTimeout(timer);
    showCard(hit, p, opening);
  });
}

// The card stays beside its event as the scroll moves.
let cardPos = "";
/** `opening`: the card has just opened, so with nothing on screen to sit beside it may take its own place. */
function placeCard(opening = false) {
  if (cardId == null) return;
  const m = 10;
  const w = card.offsetWidth || 380,
    h = card.offsetHeight || 240;
  // It keeps clear of the notes, when they are open beside the scroll.
  const right = reading.isOpen ? reading.el.getBoundingClientRect().left : innerWidth;
  const a =
    chronicle.anchorOf(cardId) ??
    (opening ? { x0: (camera.plot.left + right - w) / 2, x1: 0, y0: camera.plot.top, y1: camera.plot.top + 20 } : null);
  if (!a) return;
  // Below what it describes, else above, else beside it; always wholly on screen.
  let x = a.x0,
    y: number;
  if (a.y1 + m + h <= innerHeight - m) y = a.y1 + m;
  else if (a.y0 - m - h >= m) y = a.y0 - m - h;
  else {
    x = a.x1 + m + w <= right - m ? a.x1 + m : a.x0 - m - w;
    y = (a.y0 + a.y1) / 2 - h / 2;
  }
  x = Math.max(m, Math.min(right - w - m, x));
  y = Math.max(m, Math.min(innerHeight - h - m, y));
  const pos = `translate(${Math.round(x)}px, ${Math.round(y)}px)`;
  if (pos !== cardPos) {
    cardPos = pos;
    card.style.transform = pos;
  }
}
// The card grows as its picture arrives: place it again whenever its size changes.
new ResizeObserver(() => placeCard()).observe(card);

// ---------- Pointing ----------

function realmAt(x: number, y: number): Realm | null {
  const { plot } = camera;
  // The rolled ends count too: the realms just before and after the view live there, and are clickable.
  if (x < plot.left || x > plot.left + plot.width || y < plot.top || y > plot.top + plot.height) return null;
  const lane = camera.laneAt(y),
    year = camera.yearAt(x);
  const b = chart.blocks.find((b) => b.x0 <= lane && lane < b.x1 && b.cy0 <= year && year < b.y1);
  return b?.realm ?? null;
}

const pointer = { x: -1, y: -1, inside: false, held: false };
const light = new Spring(0, 0.25);
const needle = new Spring(0, 0.08);
const lift = new Spring(0, 0.09);
const hint = byId("hint");
let hinted = false;
function dismissHint() {
  if (hinted) return;
  hinted = true;
  hint.classList.add("gone");
  try {
    localStorage.setItem("atlas-hint", "seen");
  } catch {
    // Not remembered; it will show again next time.
  }
}
// It leaves at the first touch, or on its own after a while.
setTimeout(dismissHint, 9000);
try {
  if (localStorage.getItem("atlas-hint")) dismissHint();
} catch {
  // Storage can be blocked; the hint simply shows.
}

attachInput(canvas, camera, {
  tap(x, y) {
    dismissHint();
    if (focused) {
      const hit = chronicle.hits.find((h) => x >= h.x0 && x <= h.x1 && y >= h.y0 && y <= h.y1);
      if (hit?.age) return enterAge(hit.age);
      if (hit?.title) return openReading();
      if (hit) return openCard(hit);
    }
    if (cardId != null) return closeCard();
    const r = realmAt(x, y);
    if (r && r !== focused) go(r);
    else if (!r && focused) go(null);
  },
  point(x, y, held) {
    pointer.x = x;
    pointer.y = y;
    pointer.held = held;
    pointer.inside = x > camera.plot.left && y > camera.plot.top;
    if (pointer.inside) pinned = null;
    stage.invalidate();
  },
  leave() {
    pointer.inside = false;
    stage.invalidate();
  },
  back() {
    if (seek.isOpen) return;
    if (reading.isOpen) reading.close();
    else if (cardId != null) closeCard();
    else if (climb()) return;
    else if (focused) go(null);
    else if (land != null) goLand(null);
  },
  changed: (scrolled = true) => {
    if (scrolled) lastInput = performance.now();
    pinned = null;
    seeking = null;
    dismissHint();
    stage.invalidate();
  },
});

// ---------- The lands down the left, the ages along the top ----------

const lands = new LandsPanel(
  chart,
  (lane) => {
    dismissHint();
    goLand(lane);
  },
  (realm) => go(realm),
);
const eras = new ErasBand(chart, (era) => {
  dismissHint();
  closeCard();
  if (eras.current(camera) === era) {
    // Clicked again, the age lets go: back to whatever you were looking at before it.
    if (focused) frameRealm(focused);
    else if (land != null) flyTo(...landYears(land), 0.02);
    else flyTo(chart.axis.start, END, 0.01);
  } else flyTo(era.from, Math.min(era.to, END), 0.02);
  stage.invalidate();
});
document.body.append(lands.el, eras.el);

/** Who held each land in a year: the realm holding the whole of it, or else the first of its halves. */
function rulersAt(year: number): (Realm | null)[] {
  const out: (Realm | null)[] = chart.columns.map(() => null);
  for (const { lane, block } of text.holdersAt(year)) if (!out[lane] || !block.part) out[lane] = block.realm;
  return out;
}

let lastUnder: Hit | undefined;

// ---------- Read more: a realm's curiosities and further reading ----------

const readBtn = byId<HTMLButtonElement>("read-more");
const reading = new ReadingPanel(() => {
  readBtn.setAttribute("aria-expanded", "false");
  room.target = 0;
  stage.invalidate();
});
/**
 * Where there is width to spare, the scroll makes room for the notes, so nothing is hidden under them; on a
 * narrow screen they are laid over it instead.
 */
function roomForReading(): number {
  const w = reading.el.offsetWidth + 14;
  return innerWidth - (innerWidth < 700 ? 120 : 216) - w >= 560 ? w : 0;
}
document.body.append(reading.el);
const now = new Date().getFullYear();
function openReading() {
  const realm = focused;
  if (!realm) return;
  void loadChronicle(realm.id).then((c) => {
    if (!c || focused !== realm) return;
    closeCard();
    reading.open(realm, c.chronicle, chronicle.trail, `${spanText(realm, now)} · ${realm.tagline}`);
    readBtn.setAttribute("aria-expanded", "true");
    room.target = roomForReading();
    stage.invalidate();
  });
}
readBtn.addEventListener("click", () => {
  // Open on another age than the one it was written for? Written again for where you are now.
  const here = chronicle.trail.at(-1) ?? null;
  if (reading.isOpen && reading.about?.age === here) reading.close();
  else openReading();
});
/** The button is there only while a realm with a chronicle is open; its place is kept, so nothing shifts. */
let readable = false;
function syncReadButton() {
  const can = !!focused && chronicle.realm === focused && chronicle.pages != null;
  if (can === readable) return;
  readable = can;
  readBtn.classList.toggle("unavailable", !can);
  readBtn.disabled = !can;
}

// ---------- Seek: going straight to a realm, an age, an event, a life or a year ----------

/** A year chosen in Seek: the needle stands on it, naming every land's ruler, until you move. */
let pinned: number | null = null;
/** A card to open once the scroll has flown to it, with how often the view has closed in on it so far. */
let seeking: { realm: Realm; id: string; at: number; tries: number; quiet: number } | null = null;

function seekTo(target: Target) {
  dismissHint();
  closeCard();
  // The pointer is over Seek, not the scroll: the needle should stand where the result is.
  pointer.inside = false;
  pinned = null;
  seeking = null;
  if (target.type === "year") {
    const { year } = target;
    if (focused && !(year >= focused.home.from && year < Math.min(focused.home.to, END))) go(null);
    else if (!focused && land != null) goLand(null);
    const half = 60;
    const from = Math.max(chart.axis.start, Math.min(END - 2 * half, year - half));
    flyTo(from, from + 2 * half, 0);
    pinned = year;
    stage.invalidate();
    return;
  }
  const realm = chart.realmById.get(target.realm);
  if (!realm) return;
  if (focused !== realm) go(realm);
  else if (target.type === "realm") frameRealm(realm);
  if (target.type === "realm") return;
  void loadPages(realm.id).then((pages) => {
    if (!pages || focused !== realm) return;
    if (target.type === "age") {
      const age = pages.ages.find((a) => a.period.id === target.age);
      if (age) enterAge(age);
      return;
    }
    const { home } = realm;
    const inside = (y: number) => Math.min(Math.min(home.to, END) - 0.5, Math.max(home.from + 0.5, y));
    if (target.type === "event") {
      const h = pages.happenings.find((x) => x.event.id === target.id);
      if (!h) return;
      // Close enough that it has room for its story, with the years around it for context.
      const age = h.age ? h.age.period.to - h.age.period.from : Infinity;
      const w = Math.max(6, Math.min(40, age));
      const at = inside(h.at);
      flyTo(at - w / 2, at + w / 2, 0.04);
      // Its story is written only on the years the realm holds its home; an event just past them (the fall that
      // ended it, say) cannot be pointed to on the page, so its card opens on its own once the flight is over.
      const onPage = h.at >= home.from && h.at <= Math.min(home.to, END);
      seeking = { realm, id: target.id, at, tries: onPage ? 0 : 3, quiet: 0 };
    } else {
      const l = pages.lives.find((x) => x.person.id === target.id);
      if (!l) return;
      const a = Math.max(l.reach[0], home.from),
        b = Math.min(l.reach[1], home.to, END);
      flyTo(a, Math.max(b, a + 6), 0.25);
      seeking = { realm, id: target.id, at: inside(l.from), tries: 0, quiet: 0 };
    }
    stage.invalidate();
  });
}
const seek = new Seek(chart, seekTo);
document.body.append(seek.dialog);
byId("seek-open").addEventListener("click", () => seek.open());

/**
 * Once the flight to a chosen event or life has come to rest, its card opens beside it. If the page had no room
 * to write it, the view closes in on it, a few times at most; failing that, the card opens on its own.
 */
function settleSeeking(flying: boolean, dt: number): boolean {
  const s = seeking;
  if (!s) return false;
  if (focused !== s.realm) {
    seeking = null;
    return false;
  }
  if (flying || !chronicle.pages) {
    s.quiet = 0;
    return true;
  }
  s.quiet += dt;
  if (s.quiet < 0.12) return true;
  const hit = chronicle.anchorOf(s.id);
  if (hit) {
    seeking = null;
    openCard(hit);
  } else if (s.tries < 3) {
    s.tries++;
    s.quiet = 0;
    const w = (camera.yearAt(camera.inner.x1) - camera.yearAt(camera.inner.x0)) / 3;
    flyTo(s.at - w / 2, s.at + w / 2, 0);
  } else {
    seeking = null;
    const happening = chronicle.pages.happenings.find((h) => h.event.id === s.id);
    const life = chronicle.pages.lives.find((l) => l.person.id === s.id);
    if (happening || life) openCard({ x0: 0, y0: 0, x1: 0, y1: 0, happening, life });
  }
  return true;
}

// ---------- Frames ----------

const perf = byId<HTMLOutputElement>("perf");
perf.hidden = !new URLSearchParams(location.search).has("perf");
// With ?perf the pieces are reachable from the console, for measuring.
if (!perf.hidden) Object.assign(window, { atlas: { camera, stage, labels, chronicle, text, chart } });

stage.onFrame = (dt) => {
  camera.breathe(text.laneWeights(camera));
  const flying = camera.step(dt);
  let moving = flying;
  handOver();
  settleHandover();
  if (frameWhenQuiet) moving = true;
  // Pointing: the light, the needle, and the realm that lifts under the hand.
  light.target = pointer.inside ? 1 : 0;
  needle.target = (pointer.inside && !pointer.held) || pinned != null ? 1 : 0;
  const hover = pointer.inside && !pointer.held ? realmAt(pointer.x, pointer.y) : null;
  const hoverId = hover && hover !== focused ? hover.index : -1;
  if (realms.uniforms.uHover.value !== hoverId) {
    realms.uniforms.uHover.value = hoverId;
    lift.set(0);
  }
  lift.target = hoverId >= 0 ? 1 : 0;
  const under =
    focused && pointer.inside && !pointer.held
      ? chronicle.hits.find((h) => pointer.x >= h.x0 && pointer.x <= h.x1 && pointer.y >= h.y0 && pointer.y <= h.y1)
      : undefined;
  // The event under the pointer lights its dot and tick; its picture is fetched ahead, so its card opens whole.
  chronicle.pointed = under?.happening?.event.id ?? null;
  if (under && under !== lastUnder) prefetch(under.happening?.event.wiki ?? under.life?.person.wiki);
  lastUnder = under;
  canvas.classList.toggle("pointing", hoverId >= 0 || !!under);
  for (const s of [night, focusMix, light, needle, lift]) moving = s.step(dt, 1e-4) || moving;
  const making = room.step(dt, 0.05);
  if (making || camera.plot.width !== plotOf().width) camera.plot = plotOf();
  moving = making || moving;

  const u = stage.uniforms;
  u.uNight.value = night.value;
  u.uLight.value.set(pointer.x, pointer.y);
  u.uLightOn.value = light.value;
  realms.uniforms.uLift.value = lift.value;
  realms.uniforms.uFocusMix.value = focusMix.value;
  if (!focused && focusMix.value < 0.002) {
    realms.uniforms.uFocus.value = -1;
    text.focus = null;
    if (chronicle.realm) chronicle.show(null, null);
  }
  chronicle.opacity = focusMix.value;
  text.focusMix = focusMix.value;
  text.needleX = pinned != null && !pointer.inside ? camera.xOf(pinned + 0.001) : pointer.x;
  text.needle = needle.value;

  stage.syncCamera(camera);
  // The lands say who ruled them at the needle, or at the middle of the screen.
  const { x0: ix0, x1: ix1 } = camera.inner;
  const rulers = rulersAt(
    pinned != null && !pointer.inside
      ? pinned + 0.001
      : camera.yearAt(pointer.inside ? Math.min(ix1, Math.max(ix0, pointer.x)) : (ix0 + ix1) / 2),
  );
  lands.update(camera, (lane) => rulers[lane] ?? null, focused ? focused.home.lane : land, night.value > 0.5);
  eras.update(camera);
  moving = text.layout(camera, dt) || moving;
  moving = chronicle.layout(camera, dt) || moving;
  rules.flush();
  overlay.flush();
  const waiting = labels.flush();
  moving = settleSeeking(flying, dt) || moving;
  placeCard();
  syncTrail();
  syncReadButton();
  if (!perf.hidden) {
    const t = stage.frameTimes.slice(-120).sort((a, b) => a - b);
    perf.textContent = `cpu/frame p50 ${(t[t.length >> 1] ?? 0).toFixed(2)} ms  p95 ${(t[Math.floor(t.length * 0.95)] ?? 0).toFixed(2)} ms`;
  }
  return moving || waiting;
};

function resize() {
  stage.resize(innerWidth, innerHeight);
  if (reading.isOpen) room.target = roomForReading();
  camera.plot = plotOf();
  document.documentElement.style.setProperty("--gutter", `${camera.plot.left}px`);
  stage.invalidate();
}
window.addEventListener("resize", resize);
resize();
fromHash();
